// SparkCue marketing — static content (tight, confident, conversion-led copy).

export const STEPS = [
  {
    n: "01",
    title: "Paste your conversation",
    body: "Copy the chat from iMessage, WhatsApp, Instagram, Tinder, Bumble, Hinge or Messenger. Paste the text, or upload a screenshot and SparkCue reads it for you.",
  },
  {
    n: "02",
    title: "SparkCue reads the room",
    body: "It tracks the stage, their interest and the momentum — not just the last message.",
  },
  {
    n: "03",
    title: "Send the perfect move",
    body: "Get the best reply, alternates in different tones, and a nudge to wait when nothing is the right answer.",
  },
];

export const FEATURES = [
  { icon: "Activity", title: "Reads emotional signals", body: "Catches warmth, hesitation and interest hiding between the lines." },
  { icon: "Flame", title: "Understands attraction", body: "Knows the difference between polite and genuinely into you." },
  { icon: "Compass", title: "You pick the goal", body: "Just vibes, their number, or the date. The coaching changes to match, and you can switch any time." },
  { icon: "Zap", title: "Kills boring chats", body: "Turns dead-end replies into something they want to answer." },
  { icon: "ShieldCheck", title: "Stops over-texting", body: "Tells you when to hold back so you never look needy." },
  { icon: "Camera", title: "Upload a screenshot", body: "Can't copy the text out? Send a screenshot instead and SparkCue reads the conversation off it." },
  { icon: "CalendarHeart", title: "Moves toward dates", body: "Guides the chat from texting to actually meeting." },
];

// Batch 139 — this used to end on an invented reply from her plus a `why`
// phrased as a result: "Turned a brush-off into a booked night." Real
// coaching cards standing beside invented outcomes make the real ones read
// as invented too. What is left claims nothing about what happened: her
// message, what most people reach for, and what SparkCue would say instead.
export const EXAMPLES = [
  {
    them: "haha maybe 🙃",
    typical: "ok haha",
    spark: "that's a yes with extra steps. friday or saturday?",
  },
  {
    them: "I don't really do dating apps tbh",
    typical: "oh ok, fair enough",
    spark: "same, they're exhausting. which is exactly why we should skip the small talk and grab a drink",
  },
  {
    them: "you seem like a lot of the guys on here ngl",
    typical: "I'm not like other guys I promise",
    spark: "bold of you to assume the other guys could keep up",
  },
];

export const FAQS = [
  { q: "Is SparkCue free?", a: "Your first five replies are free. Five in total, not five a day. After that it's $9.99 a month or $59.99 a year. Cancel any time and you keep access until the period you've paid for ends." },
  { q: "Is my conversation private?", a: "Your chat is used only to craft your reply. It's never sold, shared, or posted anywhere. One thing does stick around: anything you type into the feedback box is kept, because a person reads it to fix what's not working. Don't put anything in there you wouldn't want us to hold on to." },
  { q: "Do you store my chats?", a: "Your chat history stays on your device — we don't store it. The replies we generate pass through our server to reach you and are deleted within the hour. One exception: tapping Helpful or Not helpful on a reply, or copying it, keeps that one reply on our server for 12 months. That's how we find out which replies land and which miss, so we can fix the ones that miss." },
  { q: "Which messaging apps work with SparkCue?", a: "Any app you can copy text from: iMessage, WhatsApp, Instagram, Tinder, Bumble, Hinge or Messenger." },
  { q: "Can I change the suggested reply?", a: "It's yours once you copy it. Tap to copy, then edit it in your messaging app before you send." },
  { q: "What if I don't like the first suggestion?", a: "Get instant alternates in a different tone until one sounds exactly like you." },
  { q: "How does it work?", a: "Paste your chat. SparkCue reads the full context and signals, then gives you the smartest next reply." },
  { q: "Can it help before a first date?", a: "That's the point — it moves you from texting to meeting, at the right moment." },
];

// Tiny interactive preview (no account needed). Curated, instant.
export const DEMO = [
  {
    label: "She went quiet",
    them: "sorry, been crazy busy this week 😅",
    spark: "busy people are the interesting ones. ping me when you resurface and we'll grab that drink",
    signal: "Low effort, still warm · plant the date, zero neediness",
  },
  {
    label: "She's testing you",
    them: "you're kind of full of yourself huh",
    spark: "and yet here you are, still texting back 😌",
    signal: "Playful test · match it, don't apologize",
  },
  {
    label: "Ready to meet",
    them: "we should hang out sometime fr",
    spark: "love that. thursday, that little wine bar on 5th — I'll make it easy to say yes to",
    signal: "Clear green light · close with a specific plan",
  },
];

// Batch 136 — renamed from WAITLIST_PERKS with the list itself. Three of the
// four were things offered FOR JOINING A LIST that no longer exists, so they
// were promises with nothing behind them. These are what the product does.
// "Five replies free" is section 4 of the terms, not a marketing number.
export const FREE_TIER_POINTS = [
  "Five replies free, in total",
  "No sign-up needed to try",
  "Works with any dating app",
  "Your chats stay on your device",
];

// Batch 139 — the three cards the page shows below the fold, in the app's own
// format. See marketing/CoachingCard.jsx for what they mirror and why.
//
// CARD 3 IS A DIFFERENT STATE AND CARRIES NO REPLY. Its directive and signal
// are verbatim from backend/core/coaching_engine.py's
// _SOLICITATION_STOP_INSTRUCTION and _SOLICITATION_STOP_WHY, which are the only
// strings that ever populate those fields, and its incoming messages come from
// the solicitation judge's own golden set (core/solicitation_eval_cases.py,
// case sol_patient_script) rather than from marketing's imagination.
//
// The incoming messages on cards 1 and 2 are the situation, not a result.
// Nothing after our reply is shown, because nothing after it is known.
export const COACHING_CARDS = [
  {
    id: "restraint",
    kind: "coaching",
    incoming: ["haha this was fun. i'm gonna crash though, early start"],
    headline: "she's not there, so don't chase it. one clean line and let her come to you if she wants to.",
    reasoning: "She closed warmly but she closed. Matching her energy without adding weight is what leaves the door open. Anything that asks for more spends the exit you've still got.",
    reply: "fair enough. good talking to you",
  },
  {
    id: "read",
    kind: "coaching",
    incoming: [
      "hahaha okay that's actually a good point \u{1F62D} i've been trying to get into coffee properly, like actually tasting it instead of drinking it to survive. my friend keeps saying it's about depth of flavor and i think i finally get what she means. what do you do all day btw?",
    ],
    headline: "you're good here, just keep vibing with her, don't turn it into a mission.",
    reasoning: "She gave three paragraphs and a direct question after laughing, she's leaning in. Answer what she asked, react to something specific she said.",
    reply: "i work in tech, keeps me pretty locked in during the day. depth of flavor though, that's the perfect way to put it, i'm stealing that",
  },
  {
    id: "stop",
    kind: "stop",
    incoming: [
      "the bill is 480 and i'm 200 short",
      "do you have cashapp? i'd pay you back i promise",
    ],
    directive: "stop here. she is working you for money, not talking to you. do not send anything, do not negotiate, and do not try to win her round.",
    signal: "the asks are one way, money or a paid page from you and nothing back. that is not a conversation you can turn around by saying the right thing, so there is nothing worth sending here.",
  },
];
